import {
  parseLocalBroadcastPayload,
  readCount,
  readLabel,
} from "./localBroadcastPayload";

/**
 * Stats del broadcast LOCAL (SHM, sin WebTransport). El main empuja al OSR, una vez por
 * segundo, un JSON con contadores ACUMULADOS del encoder de video (NVENC / Media Foundation /
 * VideoToolbox) y de las salidas RTMP que kero publica. Acá se valida ese JSON (viene de otro
 * proceso: cualquier campo puede faltar o venir con un tipo raro) y se derivan las tasas (fps,
 * kbps) a partir de dos muestras consecutivas.
 *
 * Contrato con el main (canal IPC "osr-broadcast-stats"):
 *   {"encoder":{"kind":"nvenc","frames":1234,"dropped":2,"bytes":51234567},
 *    "kero":{"ts":1757100000000,
 *            "outputs":[{"name":"YouTube","sink":"rtmpsink_0","outBytes":40000000,
 *                        "unackedBytes":12000,"queueMs":35}]}}
 *
 * `encoder` es null sin sesión; `kero` es null hasta que kero publica. `queueMs` es el nivel de
 * la cola previa al sink RTMP (si crece hacia 2000 la red no da abasto) y `unackedBytes` lo que
 * se mandó sin ack del servidor.
 */

export interface LocalEncoderSample {
  kind: string;
  frames: number;
  dropped: number;
  bytes: number;
}

export interface LocalOutputSample {
  name: string;
  sink: string;
  outBytes: number;
  unackedBytes: number;
  queueMs: number;
}

export interface LocalBroadcastStatsSample {
  encoder: LocalEncoderSample | null;
  kero: { ts: number; outputs: LocalOutputSample[] } | null;
}

/** `kind` cuando el main no lo manda (o manda otra cosa): la UI lo muestra tal cual. */
export const UNKNOWN_ENCODER_KIND = "unknown";

/** `name` de una salida sin nombre: nunca debería pasar, pero no rompemos por eso. */
export const DEFAULT_OUTPUT_NAME = "RTMP";

function parseEncoder(value: unknown): LocalEncoderSample | null {
  if (value === null || typeof value !== "object") return null;

  const enc = value as Record<string, unknown>;

  return {
    kind: readLabel(enc.kind, UNKNOWN_ENCODER_KIND),
    frames: readCount(enc.frames),
    dropped: readCount(enc.dropped),
    bytes: readCount(enc.bytes),
  };
}

function parseOutput(value: unknown): LocalOutputSample | null {
  if (value === null || typeof value !== "object") return null;

  const out = value as Record<string, unknown>;

  return {
    name: readLabel(out.name, DEFAULT_OUTPUT_NAME),
    sink: readLabel(out.sink, ""),
    outBytes: readCount(out.outBytes),
    unackedBytes: readCount(out.unackedBytes),
    queueMs: readCount(out.queueMs),
  };
}

function parseKero(value: unknown): LocalBroadcastStatsSample["kero"] {
  if (value === null || typeof value !== "object") return null;

  const kero = value as Record<string, unknown>;

  const outputs = Array.isArray(kero.outputs)
    ? kero.outputs
        .map(parseOutput)
        .filter((o): o is LocalOutputSample => o !== null)
    : [];

  return { ts: readCount(kero.ts), outputs };
}

export function parseLocalBroadcastStats(
  payload: unknown,
): LocalBroadcastStatsSample | null {
  const stats = parseLocalBroadcastPayload(payload);

  if (!stats) return null;

  return { encoder: parseEncoder(stats.encoder), kero: parseKero(stats.kero) };
}

/** Foto derivada del encoder: tasas sobre la muestra anterior + acumulados. */
export interface LocalEncoderSnapshot {
  kind: string;
  fps: number;
  bitrateKbps: number;
  frames: number;
  droppedFrames: number;
}

/** Foto derivada de una salida RTMP: egreso sobre la muestra anterior + nivel de cola actual. */
export interface LocalOutputSnapshot {
  name: string;
  outKbps: number;
  unackedBytes: number;
  queueMs: number;
}

export interface LocalBroadcastSnapshot {
  encoder: LocalEncoderSnapshot | null;
  outputs: LocalOutputSnapshot[];
}

/** Foto vacía NUEVA cada vez: nadie comparte el array `outputs` entre instancias o resets. */
function emptySnapshot(): LocalBroadcastSnapshot {
  return { encoder: null, outputs: [] };
}

/** kbps de un delta de bytes en un delta de ms (bytes*8/ms = bits/ms = kbps). Sin delta → 0. */
function rateKbps(deltaBytes: number, deltaMs: number): number {
  if (deltaMs <= 0 || deltaBytes <= 0) return 0;

  return Math.round((deltaBytes * 8) / deltaMs);
}

/** fps de un delta de frames en un delta de ms, con 1 decimal. Sin delta → 0. */
function rateFps(deltaFrames: number, deltaMs: number): number {
  if (deltaMs <= 0 || deltaFrames <= 0) return 0;

  return Math.round((deltaFrames * 10_000) / deltaMs) / 10;
}

interface EncoderMark {
  at: number;
  frames: number;
  bytes: number;
}

interface OutputMark {
  at: number;
  outBytes: number;
  outKbps: number;
}

/**
 * Convierte la serie de muestras acumuladas en la foto que consume la UI. La primera muestra no
 * tiene contra qué comparar: reporta los acumulados con tasas en 0. El encoder se fecha con
 * `now` (el main lee sus contadores al momento de empujar); las salidas con el `ts` de kero, que
 * es cuando kero las midió. Si kero repite el mismo `ts` (no republicó), se conserva la última
 * tasa en vez de mostrar un 0 falso.
 */
export class BroadcastStatsTracker {
  private lastEncoder: EncoderMark | null = null;

  private lastOutputs = new Map<string, OutputMark>();

  private snapshot: LocalBroadcastSnapshot = emptySnapshot();

  constructor(private readonly now: () => number = Date.now) {}

  reset(): void {
    this.lastEncoder = null;
    this.lastOutputs = new Map();
    this.snapshot = emptySnapshot();
  }

  current(): LocalBroadcastSnapshot {
    return this.snapshot;
  }

  push(sample: LocalBroadcastStatsSample): LocalBroadcastSnapshot {
    const at = this.now();

    this.snapshot = {
      encoder: this.deriveEncoder(sample.encoder, at),
      outputs: this.deriveOutputs(sample.kero, at),
    };

    return this.snapshot;
  }

  private deriveEncoder(
    encoder: LocalEncoderSample | null,
    at: number,
  ): LocalEncoderSnapshot | null {
    if (!encoder) {
      this.lastEncoder = null;

      return null;
    }

    const prev = this.lastEncoder;

    this.lastEncoder = { at, frames: encoder.frames, bytes: encoder.bytes };

    const deltaMs = prev ? at - prev.at : 0;

    return {
      kind: encoder.kind,
      fps: prev ? rateFps(encoder.frames - prev.frames, deltaMs) : 0,
      bitrateKbps: prev ? rateKbps(encoder.bytes - prev.bytes, deltaMs) : 0,
      frames: encoder.frames,
      droppedFrames: encoder.dropped,
    };
  }

  private deriveOutputs(
    kero: LocalBroadcastStatsSample["kero"],
    at: number,
  ): LocalOutputSnapshot[] {
    if (!kero) {
      this.lastOutputs = new Map();

      return [];
    }

    const keroAt = kero.ts > 0 ? kero.ts : at;

    const seen = new Map<string, OutputMark>();

    const outputs = kero.outputs.map((out) => {
      // El sink es el id estable de kero; el nombre del destino es el fallback.
      const key = out.sink || out.name;

      const prev = this.lastOutputs.get(key);

      const deltaMs = prev ? keroAt - prev.at : 0;

      const mark: OutputMark =
        prev && deltaMs <= 0
          ? prev
          : {
              at: keroAt,
              outBytes: out.outBytes,
              outKbps: prev
                ? rateKbps(out.outBytes - prev.outBytes, deltaMs)
                : 0,
            };

      seen.set(key, mark);

      return {
        name: out.name,
        outKbps: mark.outKbps,
        unackedBytes: out.unackedBytes,
        queueMs: out.queueMs,
      };
    });

    this.lastOutputs = seen;

    return outputs;
  }
}
